import { useEffect, useMemo, useState } from "react";
import {
  addEdge,
  Background,
  Connection,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from "@xyflow/react";
import useAutoLayout from "./Layout/useAutoLayout";
import {
  Dialog,
  DialogTitle,
  DialogActions,
} from "V2.0/components/DialogFullWidth";
import { Button } from "V2.0/common/button";

import usePipelineV2 from "../usePipelineV2";
import { nodeTypes } from "./Nodes";
import { edgeTypes } from "./Edges";

import NodeAdd from "./NodeAdd";
import NodeConfigure from "./NodeConfigure";
import PipelineBuilderProvider from "V2.0/HomePages/Pipelines/PipelineBuilder/PipelineBuilderProvider";
import { message } from "antd";
import { showErrorToast } from "V2.0/utils";
import { validateNodeConfig } from "./NodeConfigure/nodeValidation";
import { getNodeTypeInfo } from "./Models/nodeCatalog";
import NodeDeleteDialog from "./NodeDeleteDialog";

import useWhatsapp from "V2.0/HomePages/Integrations/Communications/Whatsapp/useWhatsapp";
import { useAssistants } from "V2.0/HomePages/Assistants";

export default PipelineBuilderV2;

const defaultNodes = [
  { id: "placeholder", type: "placeholder", data: {}, position: { x: 0, y: 0 } },
];

const defaultEdges = [];

export function getInitialNodes(nodes) {
  if (nodes.length === 0) {
    return defaultNodes;
  }

  const initialNodes = nodes.map((node) => {
    const { nodeId, type, config, name, description, position } = node;

    return {
      id: nodeId,
      data: {
        name,
        description,
        ...config,
      },
      type,
      position: {
        x: position.x ?? 0,
        y: position.y ?? 0,
      },
    };
  });

  return initialNodes;
}

export function getInitialEdges(edges) {

  const initialEdges = edges.map((edge) => {
    const { sourceNodeId, targetNodeId, sourcePort = "default" } = edge;

    return {
      id: `${sourceNodeId}->${targetNodeId}->${sourcePort}`,
      source: sourceNodeId,
      target: targetNodeId,
      sourceHandle: sourcePort,
      label: sourcePort,
      deletable: true,
      type: "default",
      data: {
        sourcePort: sourcePort,
      },
    };
  });

  return initialEdges;
}

export function getInitialNodesAndEdges(pipeline) {
  const { nodes = [], edges = [] } = pipeline ?? {};

  if (nodes.length === 0) {
    return {
      initialNodes: defaultNodes,
      initialEdges: defaultEdges,
    };
  }

  const initialNodes = getInitialNodes(nodes);
  const initialEdges = getInitialEdges(edges);

  return {
    initialNodes,
    initialEdges,
  };
}

const ConfigurationMode = {
  ADD: "add",
  CONFIGURE: "configure",
};

function PipelineBuilderV2(props) {
  const { workspaceId, pipelineId, onClose, onPipelineUpdate } = props;

  const { pipeline, isLoading, getPipeline } = usePipelineV2();

  useEffect(() => {
    if (workspaceId && pipelineId) {
      getPipeline(workspaceId, pipelineId);
    }
  }, [workspaceId, pipelineId]);

  if (isLoading) return null;

  return (
    <ReactFlowProvider>
      <PipelineBuilderV2Component
        workspaceId={workspaceId}
        pipeline={pipeline}
        onClose={onClose}
        onPipelineUpdate={onPipelineUpdate}
      />
    </ReactFlowProvider>
  );
}

function PipelineBuilderV2Component(props) {
  const { workspaceId, pipeline, onClose, onPipelineUpdate } = props;
  const { getNodes, getEdges, fitView } =
    useReactFlow();

  const [nodes, setNodes, onNodesChange] = useNodesState(
    getInitialNodes(pipeline?.nodes ?? []),
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState(
    getInitialEdges(pipeline?.edges ?? []),
  );

  const [panel, setPanel] = useState({
    mode: undefined,
    sourceNode: undefined,
    validationErrors: {},
  });
  const [loading, setLoading] = useState(false);
  const [deleteNode, setDeleteNode] = useState(undefined);

  const { getActiveWhatsappProvider, evolutionConfigurationState } =
    useWhatsapp(workspaceId);
  const { assistants, fetchAssistants } = useAssistants(workspaceId);

  const layoutOptions = useMemo(
    () => ({ direction: "TB", spacing: [100, 240] }),
    [],
  );

  // useLayout();
  useAutoLayout(layoutOptions);

  useEffect(() => {
    fetchAssistants();
  }, []);

  const handleNodeClick = (e, node) => {
    if (panel.mode) {
      return;
    }

    if (node.type === "placeholder") {
      setPanel({
        mode: ConfigurationMode.ADD,
        sourceNode: node,
        validationErrors: {},
      });
    }

    return;
  };

  const handleDirectDelete = (deletedNode) => {
    const currentNodes = getNodes();
    const currentEdges = getEdges();

    const incomingEdge = currentEdges.find((e) => e.target === deletedNode.id);
    const outgoingEdge = currentEdges.find((e) => e.source === deletedNode.id);

    const remainingEdges = currentEdges.filter(
      (e) => e.source !== deletedNode.id && e.target !== deletedNode.id,
    );

    if (incomingEdge && outgoingEdge) {
      remainingEdges.push({
        id: `${incomingEdge.source}->${outgoingEdge.target}->${incomingEdge.sourceHandle}`,
        source: incomingEdge.source,
        target: outgoingEdge.target,
        sourceHandle: incomingEdge.sourceHandle,
        label: incomingEdge.sourceHandle,
        deletable: true,
        type: "default",
        data: incomingEdge.data,
      });
    }

    setNodes(currentNodes.filter((n) => n.id !== deletedNode.id));
    setEdges(remainingEdges);
  };

  const handleCascadeDelete = (nodeToDelete) => {
    const currentNodes = getNodes();
    const currentEdges = getEdges();

    const nodeIdsToDelete = new Set();

    const collectDescendants = (nodeId) => {
      nodeIdsToDelete.add(nodeId);
      currentEdges
        .filter((e) => e.source === nodeId)
        .forEach((e) => collectDescendants(e.target));
    };

    collectDescendants(nodeToDelete.id);

    const remainingEdges = currentEdges.filter(
      (e) => !nodeIdsToDelete.has(e.source) && !nodeIdsToDelete.has(e.target),
    );

    const remainingNodes = currentNodes.filter(
      (n) => !nodeIdsToDelete.has(n.id),
    );

    setNodes(remainingNodes.length === 0 ? defaultNodes : remainingNodes);
    setEdges(remainingEdges);
  };

  const validatePipeline = () => {
    const currentNodes = getNodes();

    for (const node of currentNodes) {
      const { type, subType } = getNodeTypeInfo(node);
      const { isValid, errors } = validateNodeConfig(type, subType, node.data);

      if (isValid) continue;

      setPanel({
        mode: ConfigurationMode.CONFIGURE,
        sourceNode: node,
        validationErrors: errors,
      });
      fitView({
        nodes: [node],
        duration: 500,
        padding: 0.5,
      });

      return isValid;
    }

    return true;
  };

  const handleUpdatePipeline = () => {
    if (!validatePipeline()) {
      return;
    }

    const nodes = getNodes();
    const edges = getEdges();

    const transformedNodes = nodes.map((node) => {
      const { data, type, id, position } = node;
      return {
        nodeId: id,
        type,
        position,
        name: data.name,
        config: {
          ...data,
        },
      };
    });

    const transformedEdges = edges.map((edge) => {
      const { id, source, target, data } = edge;

      return {
        id,
        sourceNodeId: source,
        targetNodeId: target,
        sourcePort: data?.sourcePort ?? "",
      };
    });

    const lifecycleCbs = {
      init() {
        setLoading(true);
        message.loading("Saving Pipeline");
      },
      success() {
        setLoading(false);
        message.destroy();
        message.success("Pipeline Saved");
        onClose();
      },
      error(e) {
        setLoading(false);
        message.destroy();
        showErrorToast(e, "Failed to Save Pipeline");
      },
    };

    onPipelineUpdate(
      pipeline._id,
      { nodes: transformedNodes, edges: transformedEdges },
      lifecycleCbs,
    );
  };

  const handleConnection = (connection: Connection) => {
    const { source, target, sourceHandle } = connection;
    const edges = getEdges();
    const newEdge = {
      id: `${source}->${target}->${sourceHandle}`,
      source,
      target,
      sourceHandle,
      data: {
        sourcePort: sourceHandle,
      },
      label: sourceHandle,
      type: "default",
      deletable: true,
    };

    setEdges([...edges, newEdge]);
  };

  const handleIsValidConnection = (connection: any) => {
    const { source, sourceHandle } = connection as Connection;

    const edges = getEdges();

    const isSourceAlreadyExists = edges.find((edge) => {
      return edge.source === source && edge.sourceHandle === sourceHandle;
    });

    if (isSourceAlreadyExists) {
      return false;
    }

    return true;
  };

  return (
    <>
      <PipelineBuilderProvider
        value={{
          panel,
          setPanel,
          closePanel: () =>
            setPanel({
              mode: undefined,
              sourceNode: undefined,
              validationErrors: {},
            }),
          onAddNode: (node) =>
            setPanel({
              mode: ConfigurationMode.ADD,
              sourceNode: node,
              validationErrors: {},
            }),
          onConfigureNode: (node, validationErrors = {}) =>
            setPanel({
              mode: ConfigurationMode.CONFIGURE,
              sourceNode: node,
              validationErrors,
            }),
          workspaceId,
          pipelineId: pipeline._id,
          isPanelActive: !!panel.mode,
          onDirectDelete: handleDirectDelete,
          onCascadeDelete: handleCascadeDelete,
          onDelete: (node) => setDeleteNode(node),
          getActiveWhatsappProvider,
          assistants,
          evolutionConfiguration: evolutionConfigurationState,
        }}
      >
        <Dialog
          className="h-full"
          size="full"
          open={true}
          onClose={() => {}}
        >
          <div className="flex-1 flex flex-col relative">
            <DialogTitle className="shrink-0 sm:*:w-auto *:w-auto p-4 border-none shadow-sm">
              <div className="flex flex-col gap-y-2">
                <p>{pipeline.name}</p>
              </div>
            </DialogTitle>
            <div className="flex-1 min-h-0 overflow-hidden relative">
              <ReactFlow
                fitView
                panOnScroll
                zoomOnScroll
                minZoom={0.5}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onNodeClick={handleNodeClick}
                isValidConnection={handleIsValidConnection}
                onConnect={handleConnection}
                proOptions={{ hideAttribution: true }}
                defaultEdgeOptions={{ deletable: true }}
              >
                <Background />
              </ReactFlow>
            </div>
            {panel.mode === ConfigurationMode.ADD && <NodeAdd />}
            {panel.mode === ConfigurationMode.CONFIGURE && <NodeConfigure />}
            <DialogActions className="shrink-0 sm:*:w-auto *:w-auto px-4 py-3 shadow-[0_-1px_2px_-1px_rgba(0,0,0,0.1)]">
              <Button
                outline
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                loading={loading}
                disabled={loading}
                onClick={handleUpdatePipeline}
              >
                Save
              </Button>
            </DialogActions>
          </div>
        </Dialog>
      </PipelineBuilderProvider>
      {deleteNode && (
        <NodeDeleteDialog
          node={deleteNode}
          onDirectDelete={handleDirectDelete}
          onCascadeDelete={handleCascadeDelete}
          onClose={() => setDeleteNode(undefined)}
        />
      )}
    </>
  );
}
